import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Card from 'components/card';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import CustomerSearchBar from 'components/fields/CustomerSearchBar';
import { useDisclosure } from '@chakra-ui/hooks';
import { searchCustomers } from 'interfaces/customer';
import { createDevis } from 'interfaces/devis';
import { getProducts, getProductsByIds } from 'interfaces/products';
import OrderTable from 'views/orders/createOrders/components/OrderTable';
import AddProductsModal from 'views/orders/createOrders/components/AddProductModal';
import { getTotalHT, getTotalTTC, getGlobalDiscountAmount, isCustomerHT } from 'views/orders/createOrders/utils';
import { MdAddBox } from 'react-icons/md';
import { FaExternalLinkAlt } from 'react-icons/fa';

const CreateDevis = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<any>([]);
    const [products, setProducts] = useState<any>([]);
    const [customerSearchValue, setCustomerSearchValue] = useState<string>('');
    const [customerLoading, setCustomerLoading] = useState<boolean>(true);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [newDevis, setNewDevis] = useState<any>({
        items: [],
        totalPrice: 0,
        customerId: null,
        globalDiscountType: 'none',
        globalDiscountValue: 0,
    });

    const handleChange = (e: any) => {
        setNewDevis((prev: any) => ({
            ...prev,
            [e.target.id]: e.target.value,
        }));
    };

    const globalDiscount = (d: any) => ({
        globalDiscountType: d?.globalDiscountType,
        globalDiscountValue: d?.globalDiscountValue,
    });

    const updateGlobalDiscountType = (value: string) => {
        const updatedDevis = {
            ...newDevis,
            globalDiscountType: value,
            globalDiscountValue: value === 'none' ? 0 : newDevis.globalDiscountValue,
        };
        updatedDevis.totalPrice = getTotalHT(updatedDevis.items, globalDiscount(updatedDevis));
        setNewDevis(updatedDevis);
    };

    const updateGlobalDiscountValue = (value: number) => {
        const parsed = parseFloat(String(value));
        const updatedDevis = { ...newDevis, globalDiscountValue: isNaN(parsed) ? 0 : parsed };
        updatedDevis.totalPrice = getTotalHT(updatedDevis.items, globalDiscount(updatedDevis));
        setNewDevis(updatedDevis);
    };

    const updateQuantity = (product: any, value: number) => {
        const newItems = newDevis.items.map((item: any) =>
            item === product ? { ...item, quantity: parseFloat(String(value)) || 0 } : item,
        );
        setNewDevis({ ...newDevis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(newDevis)) });
    };

    const updateComment = (product: any, value: string) => {
        const newItems = newDevis.items.map((item: any) =>
            item === product ? { ...item, comment: value } : item,
        );
        setNewDevis({ ...newDevis, items: newItems });
    };

    const updateUnityPrice = (product: any, value: number) => {
        const parsed = parseFloat(String(value));
        const newItems = newDevis.items.map((item: any) =>
            item === product ? { ...item, price: isNaN(parsed) ? 0 : parsed } : item,
        );
        setNewDevis({ ...newDevis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(newDevis)) });
    };

    const updateDiscount = (product: any, value: number) => {
        const parsed = parseFloat(String(value));
        const newItems = newDevis.items.map((item: any) =>
            item === product ? { ...item, discountValue: isNaN(parsed) ? 0 : parsed } : item,
        );
        setNewDevis({ ...newDevis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(newDevis)) });
    };

    const updateDiscountType = (product: any, value: string) => {
        const newItems = newDevis.items.map((item: any) =>
            item === product
                ? { ...item, discountType: value, discountValue: value === 'none' ? 0 : item.discountValue }
                : item,
        );
        setNewDevis({ ...newDevis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(newDevis)) });
    };

    const updatePackaging = (product: any, packaging: any) => {
        const updatedItems = newDevis.items.map((item: any) => {
            if (item !== product) return item;
            if (packaging) {
                return {
                    ...item,
                    packagingRef: packaging.ref,
                    packagingUnity: packaging.unity,
                    packagingWeight: packaging.weight,
                    packagingBillByBaseUnit: packaging.billByBaseUnit || false,
                    price: parseFloat(packaging.price),
                };
            }
            const fullProduct = products.find((p: any) => p._id === item.productId);
            const resetItem = { ...item };
            delete resetItem.packagingRef;
            delete resetItem.packagingUnity;
            delete resetItem.packagingWeight;
            delete resetItem.packagingBillByBaseUnit;
            if (fullProduct) resetItem.price = parseFloat(fullProduct.price);
            return resetItem;
        });
        setNewDevis({ ...newDevis, items: updatedItems, totalPrice: getTotalHT(updatedItems, globalDiscount(newDevis)) });
    };

    const updateLotNumber = (product: any, value: string) => {
        const newItems = newDevis.items.map((item: any) =>
            item === product ? { ...item, lotNumber: value } : item,
        );
        setNewDevis({ ...newDevis, items: newItems });
    };

    const deleteItem = (item: any) => {
        const newItems = newDevis.items.filter((i: any) => i.productId !== item.productId);
        setNewDevis({ ...newDevis, items: newItems, totalPrice: getTotalHT(newItems, globalDiscount(newDevis)) });
    };

    const initCustomers = async (search: string) => {
        const res = await searchCustomers(search);
        const list: any[] = res?.customers ?? res?.docs ?? res?.data ?? [];
        setCustomers(list.map((customer: any) => ({ ...customer, company: customer.displayName || customer.company })));
        setCustomerLoading(false);
    };

    const fetchProductsPrices = async (customerId: string) => {
        if (!customerId) return;
        const res = await getProducts(1, 100, customerId);
        if (res) setProducts(res.data ?? []);
    };

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initCustomers('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setCustomerLoading(true);
        const delayDebounceFn = setTimeout(async () => {
            initCustomers(customerSearchValue);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerSearchValue]);

    useEffect(() => {
        fetchProductsPrices(newDevis.customerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newDevis.customerId]);

    const enrichProductsLookup = async (newItemProductIds: string[]) => {
        if (!newItemProductIds.length) return;
        const alreadyLoaded = new Set((products ?? []).map((p: any) => p._id));
        const toFetch = newItemProductIds.filter((id) => !alreadyLoaded.has(id));
        if (!toFetch.length) return;
        const fetched = await getProductsByIds(toFetch);
        if (fetched?.length) setProducts((prev: any) => [...(prev ?? []), ...fetched]);
    };

    const isValidDevis = (devis: any) => {
        return devis.items.length > 0 && devis.customerId && devis.totalPrice >= 0;
    };

    const getPageState = () => (!newDevis.customerId ? 'customer' : 'devis');
    const getSelectedCustomer = () => customers.find((c: any) => c._id === newDevis.customerId);

    const addDevis = async () => {
        const parsedDiscount = parseFloat(String(newDevis.globalDiscountValue));
        const res = await createDevis({
            ...newDevis,
            items: newDevis.items.map((item: any) => ({ ...item, price: item.price === '' ? 0 : item.price })),
            globalDiscountValue: isNaN(parsedDiscount) ? 0 : parsedDiscount,
        });
        if (res?.error) {
            toast.error(typeof res.error === 'string' ? res.error : 'Impossible de créer le devis');
            return;
        }
        if (res?._id) {
            toast.success(`Devis n°${res.devisNumber} créé`);
            navigate(`/devis/${res._id}`);
        }
    };

    return (
        <div className="mt-3 px-2 sm:px-4 lg:mt-0 lg:px-0">
            <AddProductsModal
                isOpen={isOpen}
                onClose={onClose}
                addProducts={(newItems: any) => {
                    setNewDevis((prev: any) => ({
                        ...prev,
                        items: [...prev.items, ...newItems],
                        totalPrice: getTotalHT([...prev.items, ...newItems], globalDiscount(prev)),
                    }));
                    enrichProductsLookup(newItems.map((i: any) => i.productId).filter(Boolean));
                    onClose();
                }}
                existingProducts={newDevis.items.map((item: any) => item._id)}
                customerId={newDevis?.customerId}
            />
            <div className="col-span-12">
                {getPageState() === 'customer' && (
                    <Card extra="w-full min-h-[400px] sm:min-h-[500px] !p-3 sm:!p-5 border">
                        <div className="h-full w-full">
                            <p className="mt-5 sm:mt-10 text-center text-sm sm:text-base">Création de devis</p>
                            <h4 className="mt-2 text-xl sm:text-2xl text-center font-bold text-navy-700 dark:text-white px-2">
                                Quel client souhaitez-vous sélectionner ?
                            </h4>
                            <div className="mt-4 flex mx-auto w-full sm:w-[90%] lg:w-[70%] z-50 px-2">
                                <CustomerSearchBar
                                    label="Client*"
                                    displayKey="company"
                                    options={customers}
                                    onSelect={(e: any) =>
                                        setNewDevis((prev: any) => ({ ...prev, customerId: e._id }))
                                    }
                                    isLoading={customerLoading}
                                    onSearch={setCustomerSearchValue}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </Card>
                )}

                {getPageState() === 'devis' && (
                    <>
                        <div
                            className="flex items-center justify-start cursor-pointer mb-2"
                            onClick={() => setNewDevis({ ...newDevis, customerId: null })}
                        >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            <p className="text-xs sm:text-sm">Choix du client</p>
                        </div>
                        <Card extra="mt-3 w-full !p-3 sm:!p-5 border">
                            <div className="h-full w-full">
                                <p className="mt-2 text-center text-sm sm:text-base px-2">
                                    Devis pour{' '}
                                    <span className="text-toola-500 cursor-pointer hover:underline break-words" onClick={() => window.open(`/customer/${newDevis.customerId}`, '_blank')}>
                                        {getSelectedCustomer()?.company}
                                    </span>
                                </p>
                                <h4 className="mt-2 text-lg sm:text-2xl text-center font-bold text-navy-700 dark:text-white px-2">
                                    Que contient le devis ?
                                </h4>

                                <OrderTable
                                    order={newDevis}
                                    handleQtyChange={updateQuantity}
                                    handlePriceChange={updateUnityPrice}
                                    handleLotNumberChange={updateLotNumber}
                                    handleDiscountChange={updateDiscount}
                                    handleCommentChange={updateComment}
                                    handleDiscountTypeChange={updateDiscountType}
                                    handlePackagingChange={updatePackaging}
                                    products={products}
                                    onDelete={deleteItem}
                                    customerId={newDevis.customerId}
                                />

                                {newDevis.items.length === 0 && (
                                    <div className="px-2">
                                        <p className="mt-5 text-base sm:text-lg text-gray-600 text-center">
                                            Aucun produit ajouté au devis
                                        </p>
                                        <div className="flex flex-wrap justify-center gap-2">
                                            <button
                                                className="flex gap-1 text-xs sm:text-sm text-white py-2 px-3 sm:px-4 bg-toola-400 rounded-full"
                                                onClick={onOpen}
                                            >
                                                <MdAddBox className="flex my-auto text-white" />
                                                Ajouter un produit
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {newDevis.items.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-3 sm:gap-5 px-1">
                                        <div className="flex gap-1">
                                            <MdAddBox size={15} className="flex my-auto text-toola-500" />
                                            <p
                                                className="flex text-xs sm:text-sm my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                                onClick={onOpen}
                                            >
                                                Ajouter un produit
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {newDevis.items.length > 0 && (
                                    <div className="mt-4 flex items-center gap-2 px-1">
                                        <p className="text-sm sm:text-base text-navy-700 dark:text-white whitespace-nowrap">
                                            Réduction globale:
                                        </p>
                                        <div className="w-[100px]">
                                            <SearchSelector
                                                displayKey="name"
                                                value={newDevis.globalDiscountType || 'none'}
                                                options={[
                                                    { name: 'Aucune', _id: 'none' },
                                                    { name: '%', _id: 'percent' },
                                                    { name: '€', _id: 'value' },
                                                ]}
                                                onSelect={(e: any) => updateGlobalDiscountType(e._id)}
                                                inputSmall={true}
                                            />
                                        </div>
                                        {newDevis.globalDiscountType && newDevis.globalDiscountType !== 'none' && (
                                            <div className="flex items-center gap-1">
                                                <InputField
                                                    extra="w-[70px]"
                                                    placeholder="0"
                                                    id="globalDiscountValue"
                                                    type="number"
                                                    value={newDevis.globalDiscountValue}
                                                    onChange={(e: any) => updateGlobalDiscountValue(e.target.value)}
                                                    inputSmall={true}
                                                />
                                                <span className="text-xs text-gray-600">
                                                    {newDevis.globalDiscountType === 'percent' ? '%' : '€'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {newDevis.items.length > 0 && (
                                    <div className="mt-4 flex flex-wrap gap-3 sm:gap-4 lg:gap-6 px-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">Total HT:</p>
                                            <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                                {newDevis.totalPrice.toFixed(2)}€
                                            </p>
                                        </div>
                                        {getGlobalDiscountAmount(newDevis.items, newDevis.globalDiscountType, newDevis.globalDiscountValue) > 0 && (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">Réduction globale:</p>
                                                <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                                    -{getGlobalDiscountAmount(newDevis.items, newDevis.globalDiscountType, newDevis.globalDiscountValue).toFixed(2)}€
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 min-w-0">
                                            <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">Total TTC:</p>
                                            <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                                {getTotalTTC(newDevis.items, { isCustomerHT: isCustomerHT(getSelectedCustomer()), ...globalDiscount(newDevis) }).toFixed(2)}€
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </>
                )}

                {getPageState() === 'devis' && (
                    <div className="mt-3 grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-3">
                        <Card extra="w-full !p-3 sm:!p-5 border col-span-1 lg:col-span-8">
                            <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                Informations finales
                            </h4>
                            <div className="mt-5 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                                <div className="col-span-12 lg:col-span-4">
                                    <InputField
                                        label="Date de validité"
                                        placeholder="01/01/2026"
                                        id="validUntil"
                                        type="date"
                                        onChange={handleChange}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="col-span-12 lg:col-span-4">
                                    <InputField
                                        label="Date de livraison souhaitée"
                                        placeholder="01/01/2026"
                                        id="deliveryDate"
                                        type="date"
                                        onChange={handleChange}
                                        inputSmall={true}
                                    />
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="col-span-1">
                                    <InputField
                                        label="Note du client"
                                        placeholder="Conditions particulières..."
                                        id="customerNotes"
                                        type="text"
                                        onChange={handleChange}
                                        inputSmall={true}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <InputField
                                        label="Note interne"
                                        placeholder="Contexte commercial..."
                                        id="internalNotes"
                                        type="text"
                                        onChange={handleChange}
                                        inputSmall={true}
                                    />
                                </div>
                            </div>
                        </Card>
                        <Card extra="w-full !p-3 sm:!p-5 border col-span-1 lg:col-span-4">
                            <h4 className="text-base sm:text-lg font-medium text-navy-700">Client</h4>
                            <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                {getSelectedCustomer() && `Nom: ${getSelectedCustomer()?.company}`}
                            </p>
                            <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                {getSelectedCustomer() && `Email: ${getSelectedCustomer()?.mail}`}
                            </p>
                            <p
                                className="flex gap-2 mt-2 text-xs sm:text-sm text-toola-500 hover:cursor-pointer break-words"
                                onClick={() => window.open(`/customer/${getSelectedCustomer()?._id}`, '_blank')}
                            >
                                <FaExternalLinkAlt size={12} className="flex my-auto" />
                                Voir la fiche client
                            </p>
                        </Card>
                    </div>
                )}

                {getPageState() === 'devis' && (
                    <div className="mt-5 px-2 sm:px-0 sm:ml-5 grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4">
                        <button
                            className={`linear col-span-1 sm:col-span-4 lg:col-span-3 flex items-center justify-center rounded-xl px-6 sm:px-10 py-3 sm:py-2 text-sm sm:text-xs font-medium text-white ${
                                isValidDevis(newDevis)
                                    ? 'bg-[#0260CB] hover:bg-[#01499b] active:bg-[#024086]'
                                    : 'bg-[#32383D] hover:bg-[#32383D] active:bg-[#32383D] cursor-default'
                            }`}
                            onClick={() => isValidDevis(newDevis) && addDevis()}
                        >
                            Créer le devis
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateDevis;
